import { useCallback, useEffect, useMemo, useState } from "react"

import { Flex, Modal, Typography } from "antd"
import dayjs from "dayjs"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"

import ButtonComponent from "../../../../Components/Button"
import SelectComponent from "../../../../Components/Select"
import Table from "../../../../Components/Table"
import { LOG_DEFAULT_FILTERS, LOG_PAGE_SIZE } from "../../redux/paymentMatching.slice"
import {
  getPaymentMatchingLogs,
  getPaymentMatchingLogUsers
} from "../../redux/paymentMatching.thunks"
import FallbackMarker from "../IconCell/FallbackMarker"

import styles from "./ActivityLogModal.module.less"

const { Text, Title } = Typography

/**
 * The activity log: one modal, two modes.
 *
 * Opened from the page header it covers every change on the page; opened from a row's Action menu it
 * is scoped to that raw value, the subtitle names it, and the Value column is dropped — the value is
 * already in the subtitle, so repeating it in every row would waste the widest part of the table.
 *
 * Strictly read-only. There is no revert, no restore, no export, and no free-text search: the three
 * dropdowns are the only way to narrow it, and the order is fixed at newest first.
 */
const ActivityLogModal = ({ open, record, onClose }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const logs = useSelector((state) => state.paymentMatching.logs)
  const fetching = useSelector((state) => state.paymentMatching.logsFetching)
  const logUsers = useSelector((state) => state.paymentMatching.logUsers)

  const [filters, setFilters] = useState(LOG_DEFAULT_FILTERS)
  const [page, setPage] = useState(1)
  // Opening one dropdown closes the other two, and closing the modal closes whichever was left open.
  const [openFilter, setOpenFilter] = useState(null)

  const isRowLevel = !!record

  useEffect(() => {
    if (!open) return

    // Reopening starts from the defaults rather than from whatever was left selected last time.
    setFilters(LOG_DEFAULT_FILTERS)
    setPage(1)
    setOpenFilter(null)
    dispatch(getPaymentMatchingLogUsers())
  }, [open, record?.id, dispatch])

  useEffect(() => {
    if (!open) return

    dispatch(
      getPaymentMatchingLogs({
        id: record?.id,
        page,
        ...filters
      })
    )
  }, [open, record?.id, page, filters, dispatch])

  const changeFilter = useCallback((key, value) => {
    setFilters((current) => ({ ...current, [key]: value }))
    setPage(1)
  }, [])

  // One prop bundle per dropdown, so the three cannot drift apart.
  const dropdownProps = useCallback(
    (key) => ({
      className: styles.filter,
      open: openFilter === key,
      onDropdownVisibleChange: (isOpen) => setOpenFilter(isOpen ? key : null)
    }),
    [openFilter]
  )

  useEffect(() => {
    if (!openFilter) return undefined

    const containWheel = (event) => {
      if (event.target?.closest?.(".ant-select-dropdown")) event.preventDefault()
    }

    document.addEventListener("wheel", containWheel, { passive: false })

    return () => document.removeEventListener("wheel", containWheel)
  }, [openFilter])

  const clearFilters = useCallback(() => {
    setFilters(LOG_DEFAULT_FILTERS)
    setPage(1)
  }, [])

  const hasNonDefaultFilters = useMemo(
    () => Object.keys(LOG_DEFAULT_FILTERS).some((key) => filters[key] !== LOG_DEFAULT_FILTERS[key]),
    [filters]
  )

  const renderChange = useCallback(
    (entry) => {
      const emptyMarker = (
        <Text type="secondary" className={styles.empty}>
          {t("empty")}
        </Text>
      )

      if (entry.changed_field === "icon") {
        return (
          <Flex align="center" gap={10}>
            {entry.icon_before_url ? (
              <img src={entry.icon_before_url} alt="" className={styles.thumb} />
            ) : (
              <FallbackMarker />
            )}
            <Text type="secondary">→</Text>
            {entry.icon_after_url ? (
              <img src={entry.icon_after_url} alt="" className={styles.thumb} />
            ) : (
              <FallbackMarker />
            )}
          </Flex>
        )
      }

      return (
        <Flex align="center" gap={8} wrap="wrap">
          {entry.ui_name_before ? <Text delete>{entry.ui_name_before}</Text> : emptyMarker}
          <Text type="secondary">→</Text>
          {entry.ui_name_after ? <Text strong>{entry.ui_name_after}</Text> : emptyMarker}
        </Flex>
      )
    },
    [t]
  )

  const columns = useMemo(
    () =>
      [
        {
          key: "created",
          dataIndex: "created",
          title: () => t("Date & time"),
          render: (created) => (
            <div className={styles.moment}>
              <Text>{dayjs(created).format("DD MMM, YYYY")}</Text>
              <Text type="secondary" className={styles.time}>
                {dayjs(created).format("HH:mm:ss")}
              </Text>
            </div>
          )
        },
        {
          key: "user_email",
          dataIndex: "user_email",
          className: styles.emailCell,
          title: () => t("User"),
          render: (email) => <Text ellipsis={{ tooltip: email }}>{email || "—"}</Text>
        },
        // Hidden in row-level mode: the subtitle already names the value.
        !isRowLevel && {
          key: "value",
          dataIndex: "value",
          className: styles.valueCell,
          title: () => t("Value"),
          render: (value) => <Text ellipsis={{ tooltip: value }}>{value}</Text>
        },
        {
          key: "changed_field",
          dataIndex: "changed_field",
          className: styles.nowrapCell,
          title: () => t("Field"),
          render: (field) => <Text>{field === "icon" ? t("Icon") : t("UI name")}</Text>
        },
        {
          key: "change",
          title: () => t("Change"),
          render: (_, entry) => renderChange(entry)
        }
      ].filter(Boolean),
    [t, isRowLevel, renderChange]
  )

  const isEmpty = !fetching && !logs.results?.length

  return (
    <Modal
      open={open}
      onCancel={() => {
        setOpenFilter(null)
        onClose()
      }}
      // Every modal gets a patterned background unless it opts out with clear-bg.
      classNames={{ content: "clear-bg" }}
      footer={null}
      width={900}
      title={
        <Title level={5} className={styles.title}>
          {t("Activity log")}
          <Text type="secondary" className={styles.subtitle}>
            {isRowLevel ? record.current_name : t("All changes made on this page")}
          </Text>
        </Title>
      }
    >
      <Flex gap={12} wrap="wrap" className={styles.filters}>
        <SelectComponent
          {...dropdownProps("date_range")}
          value={filters.date_range}
          onChange={(value) => changeFilter("date_range", value)}
          options={[
            { value: "last_7_days", label: t("Last 7 days") },
            { value: "last_30_days", label: t("Last 30 days") },
            { value: "all_time", label: t("All time") }
          ]}
        />
        <SelectComponent
          {...dropdownProps("field")}
          value={filters.field}
          onChange={(value) => changeFilter("field", value)}
          options={[
            { value: "", label: t("All changes") },
            { value: "ui_name", label: t("UI name") },
            { value: "icon", label: t("Icon") }
          ]}
        />
        <SelectComponent
          {...dropdownProps("user")}
          value={filters.user}
          onChange={(value) => changeFilter("user", value)}
          options={[
            { value: "", label: t("All users") },
            ...logUsers.map((admin) => ({ value: String(admin.id), label: admin.email }))
          ]}
        />
      </Flex>

      {isEmpty ? (
        <Flex vertical align="center" gap={12} className={styles.emptyState}>
          <Text type="secondary">{t("No changes match the current filters.")}</Text>
          {hasNonDefaultFilters && (
            <ButtonComponent type="primary" onClick={clearFilters}>
              {t("Clear filters")}
            </ButtonComponent>
          )}
        </Flex>
      ) : (
        <Table
          rowKey="id"
          loading={fetching}
          dataSource={logs.results}
          columns={columns}
          showSizeChanger={false}
          pagination={{ total: logs.count, current: page, pageSize: LOG_PAGE_SIZE }}
          onChange={(pagination) => setPage(pagination?.current || 1)}
          scroll={{ x: "max-content" }}
        />
      )}
    </Modal>
  )
}

export default ActivityLogModal
